import * as React from "react";
import styled from "@emotion/styled";
import { keyframes, Theme } from "@emotion/react";
import { hexToRGB } from "styles/styleUtils";
import { reviewTagCSS } from "./ReviewTag";

const Label = styled.label(({ theme }) => ({
  ...theme.fonts.body,
  alignItems: "baseline",
  cursor: "pointer",
  display: "flex",
  fontWeight: "400",
  gap: theme.spacing.sm,
  userSelect: "none",
  "&:hover input": {
    transform: "translate(2px, 2px)",
  },
  "&:active input": {
    transform: "scale(0.9, 1.1)",
  },
  "& + &": {
    marginTop: theme.spacing.md,
  },
}));

const borderWidth = 2;

const pulseEffect = (color: string) =>
  keyframes({
    "0%": {
      opacity: 1,
      transform: "scale3d(0.3, 0.3, 1)",
    },
    "80%": {
      boxShadow: `inset 0 0 0 2px ${hexToRGB(color, 0.8)}`,
      opacity: 0.1,
    },
    "100%": {
      boxShadow: `inset 0 0 0 2px ${hexToRGB(color, 0.8)}`,
      opacity: 0,
      transform: "scale3d(1.2, 1.2, 1)",
    },
  });

const CheckboxControl = styled.input(({ theme }) => ({
  ...theme.fonts.body,
  appearance: "none",
  background: "rgba(0,0,0,0)",
  border: `${borderWidth}px solid currentColor`,
  borderRadius: theme.borderRadius.xs,
  color: "currentcolor",
  cursor: "pointer",
  display: "grid",
  font: "inherit",
  height: "1.5em",
  margin: 0,
  minHeight: "1.5em",
  minWidth: "1.5em",
  placeContent: "center",
  transition:
    "transform 0.15s ease, background 0.15s ease, border-color 0.15s ease",
  width: "1.5em",
  "&::before": {
    content: "''",
    // Fallback for high-contrast mode
    backgroundColor: "CanvasText",
    // Better results than background which might be removed (e.g. printing)
    boxShadow: "inset 1em 1em currentColor",
    // For the checkmark shape
    clipPath: "polygon(43% 71%, 84% 14%, 95% 24%, 43% 95%, 11% 54%, 23% 45%)",
    height: "1em",
    // Start hidden, only visible when [checked]
    opacity: 0,
    transition: "opacity 0.2s ease",
    width: "1em",
  },
  "&:checked": {
    background: theme.colors.bg,
    // borderColor: theme.colors.bg,
    "&::before": {
      opacity: 1,
    },
  },
  // "&:focus-visible": {
  //   outline: `${borderWidth}px solid ${theme.colors.fg}`,
  //   outlineOffset: `${borderWidth}px`,
  // },

  "&::after": {
    content: "''",
    position: "absolute",
    top: "50%",
    left: "50%",
    margin: "-30px 0 0 -30px",
    width: 60,
    height: 60,
    borderRadius: theme.borderRadius.md,
    opacity: 0,
    pointerEvents: "none",
  },
  "&:checked::after": {
    boxShadow: `inset 0 0 0 30px ${hexToRGB(theme.colors.bg, 0)}`,
    animation: `${pulseEffect(theme.colors.bg)} 0.5s ease-out forwards`,
  },
}));

type CheckboxProps = {
  label: React.ReactNode;
  className?: string;
} & React.ComponentProps<typeof CheckboxControl>;

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, className, ...props }, ref) => (
    <Label className={className}>
      <CheckboxControl type="checkbox" {...props} ref={ref} />
      <div>{label}</div>
    </Label>
  )
);

type TagCheckboxVariants = {
  [key: string]: (theme: Theme) => [string, string];
};

/**
 * Each variant returns a `[borderColor, background]` pair from the Theme
 */
const tagCheckboxVariants: TagCheckboxVariants = {
  default: (theme) => [theme.colors.gray60, theme.colors.gray20],
  founder: (theme) => [theme.colors.yellow, theme.colors.yellow],
  community: (theme) => [theme.colors.pink, theme.colors.pink],
  roadmap: (theme) => [theme.colors.green, theme.colors.green],
  art: (theme) => [theme.colors.purple, theme.colors.purple],
  founderAttribute: (theme) => [theme.colors.purple, theme.gradients.linear],
  missionOrCause: (theme) => [theme.colors.green, theme.gradients.linear2],
};

interface TagCheckboxProps {
  variant: keyof typeof tagCheckboxVariants;
}

const TagCheckbox = styled(Checkbox, {
  shouldForwardProp: (prop) => prop !== "variant",
})<TagCheckboxProps>(({ variant, theme }) => {
  const [borderColor, background] = tagCheckboxVariants[variant](theme);

  return {
    ...reviewTagCSS(theme),
    ...theme.fonts.body,
    border: "0",
    fontSize: theme.fontSizes.xs,
    margin: ".5rem .5rem .5rem 0",
    paddingTop: ".25em",
    paddingBottom: ".2em",
    paddingRight: "1.25em",
    position: "relative",
    gap: 0,
    // overflow: "hidden",
    "&+&": {
      marginTop: 0,
    },
    "&:hover input": {
      transform: "none",
    },
    input: {
      background: "transparent",
      border: 0,
      borderRadius: 0,
      width: "1.25em",
    },
    "input:focus": {
      outline: 0,
    },
    "input:focus::after": {
      border: "2px solid #262626",
    },
    "input:checked:focus::after": {
      border: "2px solid #262626",
    },
    "input::before": {
      width: "14px",
      height: "14px",
      pointerEvents: "none",
      transform: "translate(0, -1px)",
      transitionProperty: "opacity, transform",
      zIndex: 9,
    },
    "input:checked": {
      background: "transparent",
    },
    "input:checked::before": {
      transform: "translate(3px, -1px)",
    },
    "input::after": {
      all: "initial",
      animation: "none",
      backgroundColor: "transparent",
      bottom: "0",
      border: "2px solid",
      borderColor,
      borderRadius: theme.borderRadius.round,
      content: "''",
      left: "0",
      margin: 0,
      position: "absolute",
      right: "0",
      top: "0",
      pointerEvents: "none",
      transition: "all 0.15s ease",
    },
    "input:hover::after": {
      borderColor,
    },
    "input[disabled]::after": {
      borderColor: theme.colors.gray20,
      color: theme.colors.gray20,
    },
    "input:checked::after": {
      animation: "none",
      background,
      borderColor,
    },
    div: {
      ...theme.fonts.body,
      pointerEvents: "none",
      position: "relative",
      transition: "transform 0.15s ease",
      zIndex: 2,
    },
    "input:checked+div": {
      transform: "translateX(4px)",
    },
  };
});

export { Checkbox, TagCheckbox, CheckboxControl };
